import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../utils/axios';
import {
  BarChart3, TrendingUp, AlertTriangle, Shield, Users, Clock,
  FileText, Lock, Mail, Download, Loader2, CheckCircle, XCircle,
  Target, Timer, UserCheck, Briefcase, Activity, ArrowUp, ArrowDown,
  Settings, FileDown
} from 'lucide-react';
import ActivityHeatmap from '../components/ActivityHeatmap';

const ExecutiveDashboard = () => {
  const [data, setData] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [visibleWidgets, setVisibleWidgets] = useState([
    'alertas_kpis', 'casos_kpis', 'sla', 'carga_analista',
    'solicitudes_bloqueos', 'tendencia', 'comparacion', 'top_cuentas', 'severidad',
    'heatmap'
  ]);
  const dashboardRef = useRef(null);
  const [branding, setBranding] = useState(null);

  const WIDGET_LABELS = {
    alertas_kpis: 'KPIs de Alertas',
    casos_kpis: 'KPIs de Casos',
    sla: 'SLA - Casos Vencidos',
    carga_analista: 'Carga por Analista',
    solicitudes_bloqueos: 'Solicitudes y Bloqueos',
    tendencia: 'Tendencia 14 dias',
    comparacion: 'Comparacion Temporal',
    top_cuentas: 'Top Cuentas Alertadas',
    severidad: 'Alertas por Severidad',
    heatmap: 'Heatmap de Actividad',
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [summaryRes, compRes, trendRes, widgetsRes, brandingRes] = await Promise.all([
        api.get('/executive/summary'),
        api.get('/executive/comparison'),
        api.get('/executive/trend?dias=14'),
        api.get('/config/executive-widgets').catch(() => ({ data: null })),
        api.get('/branding').catch(() => ({ data: null })),
      ]);
      setData(summaryRes.data);
      setComparison(compRes.data);
      setTrend(trendRes.data);
      if (widgetsRes.data?.widgets_visibles) {
        setVisibleWidgets(widgetsRes.data.widgets_visibles);
      }
      if (brandingRes.data) {
        setBranding(brandingRes.data);
      }
    } catch (err) {
      console.error('Error cargando dashboard ejecutivo:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleWidget = async (widgetId) => {
    const updated = visibleWidgets.includes(widgetId)
      ? visibleWidgets.filter(w => w !== widgetId)
      : [...visibleWidgets, widgetId];
    setVisibleWidgets(updated);
    try {
      await api.put('/config/executive-widgets', { widgets_visibles: updated });
    } catch (e) { console.error(e); }
  };

  const isVisible = (id) => visibleWidgets.includes(id);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      const empresaNombre = branding?.nombre_empresa || 'AlertGuard';
      
      // Crear PDF en orientación vertical (portrait) A4
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentW = pageW - margin * 2;
      
      // Helper: agregar header en cada página
      const addPageHeader = (pageNum) => {
        // Barra superior oscura
        pdf.setFillColor(30, 41, 59); // slate-800
        pdf.rect(0, 0, pageW, 16, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Informe Ejecutivo - ${empresaNombre}`, margin, 10);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Generado: ${new Date().toLocaleString('es-AR')}`, pageW - margin, 10, { align: 'right' });
        
        // Línea azul de acento
        pdf.setDrawColor(37, 99, 235); // blue-600
        pdf.setLineWidth(0.8);
        pdf.line(margin, 17, pageW - margin, 17);
        
        // Footer
        pdf.setTextColor(148, 163, 184); // slate-400
        pdf.setFontSize(6);
        pdf.text(`Pagina ${pageNum} | Confidencial | ${empresaNombre}`, pageW / 2, pageH - 5, { align: 'center' });
      };
      
      // Helper: capturar una sección del dashboard como imagen
      const captureSection = async (selector, scale = 2) => {
        const el = dashboardRef.current?.querySelector(selector);
        if (!el) return null;
        try {
          const canvas = await html2canvas(el, { 
            scale, 
            useCORS: true, 
            backgroundColor: '#ffffff',
            logging: false 
          });
          return canvas.toDataURL('image/png');
        } catch {
          return null;
        }
      };
      
      // Definir secciones con sus selectores
      const sections = dashboardRef.current?.children;
      if (!sections) { setExporting(false); return; }
      
      // Capturar cada sección hijo del dashboard por separado
      const sectionImages = [];
      for (let i = 0; i < sections.length; i++) {
        const child = sections[i];
        if (child.offsetHeight < 10) continue; // Skip hidden
        try {
          const canvas = await html2canvas(child, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false
          });
          const imgData = canvas.toDataURL('image/png');
          const imgW = contentW;
          const imgH = (canvas.height * imgW) / canvas.width;
          sectionImages.push({ img: imgData, w: imgW, h: imgH, origH: canvas.height });
        } catch {
          // Skip failed sections
        }
      }
      
      // Distribuir imágenes en páginas
      let currentY = 22; // Start after header
      let pageNum = 1;
      addPageHeader(pageNum);
      
      for (const section of sectionImages) {
        const availableH = pageH - currentY - 10; // 10mm bottom margin
        
        if (section.h > availableH && currentY > 25) {
          // Nueva página
          pdf.addPage();
          pageNum++;
          addPageHeader(pageNum);
          currentY = 22;
        }
        
        // Si la sección es demasiado alta, escalar para que entre en la página
        let imgH = section.h;
        let imgW = section.w;
        const maxH = pageH - 32; // max height minus headers/footers
        
        if (imgH > maxH) {
          const scaleFactor = maxH / imgH;
          imgH = maxH;
          imgW = section.w * scaleFactor;
        }
        
        pdf.addImage(section.img, 'PNG', margin, currentY, imgW, imgH);
        currentY += imgH + 4; // 4mm gap
      }
      
      pdf.save(`Informe_Ejecutivo_${empresaNombre}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) { console.error('Error exporting PDF:', e); }
    finally { setExporting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-slate-400" size={40} />
      </div>
    );
  }

  if (!data) return <p className="text-center py-20 text-slate-400">No se pudieron cargar los datos</p>;

  const kpis = data.kpis || {};
  const casos = data.casos || {};
  const solicitudes = data.solicitudes || {};
  const bloqueos = data.bloqueos || {};
  const comp = comparison || {};

  const MetricCard = ({ icon: Icon, label, value, subValue, color, trend: trendDir }) => (
    <Card className={`p-4 border-l-4 ${color}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
          {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
        </div>
        <div className="flex items-center gap-1">
          <Icon size={20} className="text-slate-400" />
          {trendDir === 'up' && <ArrowUp size={14} className="text-red-500" />}
          {trendDir === 'down' && <ArrowDown size={14} className="text-green-500" />}
        </div>
      </div>
    </Card>
  );

  const maxTrend = Math.max(...(trend || []).map(t => t.total || 0), 1);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard Ejecutivo</h1>
          <p className="text-sm text-slate-500">KPIs de compliance, casos, solicitudes y bloqueos</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowConfig(!showConfig)} variant="outline" size="sm" data-testid="config-widgets-btn">
            <Settings size={16} className="mr-2" /> Configurar
          </Button>
          <Button onClick={handleExportPDF} disabled={exporting} data-testid="export-exec-pdf">
            {exporting ? <Loader2 size={16} className="animate-spin mr-2" /> : <FileDown size={16} className="mr-2" />}
            {exporting ? 'Generando...' : 'Exportar Informe'}
          </Button>
        </div>
      </div>

      {/* Config Panel */}
      {showConfig && (
        <Card className="p-4 bg-slate-50 border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-slate-700">Widgets Visibles</h3>
            <button onClick={() => setShowConfig(false)} className="text-slate-400 hover:text-slate-600">
              <XCircle size={18} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(WIDGET_LABELS).map(([id, label]) => (
              <label key={id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                isVisible(id) ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-slate-200'
              }`}>
                <input
                  type="checkbox"
                  checked={isVisible(id)}
                  onChange={() => toggleWidget(id)}
                  className="rounded border-slate-300 text-blue-600"
                />
                <span className="text-xs text-slate-700">{label}</span>
              </label>
            ))}
          </div>
        </Card>
      )}

      <div ref={dashboardRef} className="space-y-6">
        {/* === ALERTAS KPIs === */}
        {isVisible('alertas_kpis') && (
        <div>
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <AlertTriangle size={16} /> Alertas
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <MetricCard icon={AlertTriangle} label="Total Alertas" value={kpis.total_alertas || 0} color="border-l-amber-500" />
            <MetricCard icon={Clock} label="Ultimas 24h" value={kpis.alertas_24h || 0} color="border-l-blue-500" />
            <MetricCard icon={Activity} label="Ultimos 7d" value={kpis.alertas_7d || 0} color="border-l-indigo-500" />
            <MetricCard icon={XCircle} label="Criticas Abiertas" value={kpis.alertas_criticas_abiertas || 0} color="border-l-red-500" />
            <MetricCard icon={Target} label="Tasa Resolucion" value={`${kpis.tasa_resolucion || 0}%`} color="border-l-green-500" />
          </div>
        </div>
        )}

        {/* === CASOS KPIs === */}
        {isVisible('casos_kpis') && (
        <div>
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Briefcase size={16} /> Casos
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <MetricCard icon={Briefcase} label="Total Casos" value={casos.total || 0} color="border-l-slate-500" />
            <MetricCard icon={Clock} label="Abiertos" value={casos.abiertos || 0} color="border-l-blue-500" />
            <MetricCard icon={Shield} label="En Investigacion" value={casos.en_investigacion || 0} color="border-l-indigo-500" />
            <MetricCard icon={CheckCircle} label="Tasa Resolucion (Ops)" value={`${casos.tasa_cierre || 0}%`} subValue="Incluye bloqueos y derivaciones" color="border-l-green-500" />
            <MetricCard icon={Timer} label="Resolucion Prom." value={`${casos.tiempo_promedio_resolucion_dias || 0}d`} subValue="dias promedio" color="border-l-purple-500" />
            <MetricCard icon={AlertTriangle} label="Escalados" value={casos.escalados || 0} color="border-l-red-500" />
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* SLA */}
          {isVisible('sla') && (
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <Timer size={16} className="text-red-500" /> SLA - Casos Vencidos
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-amber-800">Mas de 7 dias sin resolver</p>
                  <p className="text-xs text-amber-600">Requieren atencion prioritaria</p>
                </div>
                <span className="text-3xl font-bold text-amber-700">{casos.sla?.vencidos_7d || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-red-800">Mas de 30 dias sin resolver</p>
                  <p className="text-xs text-red-600">Criticos - requieren escalamiento</p>
                </div>
                <span className="text-3xl font-bold text-red-700">{casos.sla?.vencidos_30d || 0}</span>
              </div>
            </div>
          </Card>
          )}

          {/* Carga por analista */}
          {isVisible('carga_analista') && (
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <Users size={16} className="text-blue-500" /> Carga por Analista
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(casos.por_responsable || []).map((r, i) => {
                const maxCasos = Math.max(...(casos.por_responsable || []).map(x => x.casos), 1);
                return (
                  <div key={i} className="flex items-center gap-2">
                    <UserCheck size={14} className="text-slate-400 flex-shrink-0" />
                    <span className="text-xs text-slate-600 w-32 truncate">{r.nombre}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full flex items-center justify-end pr-1"
                        style={{ width: `${Math.max((r.casos / maxCasos) * 100, 12)}%` }}>
                        <span className="text-[10px] font-bold text-white">{r.casos}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {(!casos.por_responsable || casos.por_responsable.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">Sin datos</p>
              )}
            </div>
          </Card>
          )}

          {/* Solicitudes y Bloqueos */}
          {isVisible('solicitudes_bloqueos') && (
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <FileText size={16} className="text-purple-500" /> Solicitudes & Bloqueos
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-purple-700">{solicitudes.total || 0}</p>
                  <p className="text-xs text-purple-600">Solicitudes</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-700">{bloqueos.cuentas_bloqueadas || 0}</p>
                  <p className="text-xs text-red-600">Bloqueadas</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-1 text-center">
                <div className="bg-amber-50 rounded p-2">
                  <p className="text-lg font-bold text-amber-700">{solicitudes.pendientes || 0}</p>
                  <p className="text-[10px] text-amber-600">Pendientes</p>
                </div>
                <div className="bg-green-50 rounded p-2">
                  <p className="text-lg font-bold text-green-700">{solicitudes.completadas || 0}</p>
                  <p className="text-[10px] text-green-600">Completadas</p>
                </div>
                <div className="bg-blue-50 rounded p-2">
                  <p className="text-lg font-bold text-blue-700">{solicitudes.tasa_completado || 0}%</p>
                  <p className="text-[10px] text-blue-600">Completado</p>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2 border-t text-xs">
                <span className="flex items-center gap-1 text-green-600">
                  <Mail size={12} /> {solicitudes.emails_enviados || 0} emails
                </span>
                <span className="flex items-center gap-1 text-orange-600">
                  <Lock size={12} /> {bloqueos.bloqueos_30d || 0} bloqueos/30d
                </span>
              </div>
            </div>
          </Card>
          )}
        </div>

        {/* Tendencia 14 días */}
        {isVisible('tendencia') && (
        <Card className="p-5">
          <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp size={16} /> Tendencia de Alertas - 14 dias
          </h3>
          <div className="flex items-end gap-1 h-32">
            {(trend || []).map((d, i) => (
              <div key={d.fecha || `trend-${i}`} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[9px] font-medium text-slate-600">{d.total || 0}</span>
                <div className="w-full bg-slate-100 rounded-t relative" style={{ height: '100px' }}>
                  <div
                    className="absolute bottom-0 w-full rounded-t bg-gradient-to-t from-blue-600 to-blue-400"
                    style={{ height: `${Math.max((d.total / maxTrend) * 100, 3)}%` }}
                  />
                  {d.criticas > 0 && (
                    <div
                      className="absolute bottom-0 w-full rounded-t bg-red-500 opacity-70"
                      style={{ height: `${Math.max((d.criticas / maxTrend) * 100, 3)}%` }}
                    />
                  )}
                </div>
                <span className="text-[9px] text-slate-400">{d.dia}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 justify-end">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded" /> Total</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded" /> Criticas</span>
          </div>
        </Card>
        )}

        {/* Comparación temporal */}
        {isVisible('comparacion') && comp && comp.periodo_actual && (
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <BarChart3 size={16} /> Comparacion Temporal
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Alertas', actual: comp.periodo_actual?.total, anterior: comp.periodo_anterior?.total },
                { label: 'Criticas', actual: comp.periodo_actual?.criticas, anterior: comp.periodo_anterior?.criticas },
                { label: 'Altas', actual: comp.periodo_actual?.altas, anterior: comp.periodo_anterior?.altas },
                { label: 'Resueltas', actual: comp.periodo_actual?.resueltas, anterior: comp.periodo_anterior?.resueltas },
              ].map((item, i) => {
                const diff = (item.actual || 0) - (item.anterior || 0);
                return (
                  <div key={item.label} className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">{item.label}</p>
                    <p className="text-xl font-bold text-slate-800">{item.actual || 0}</p>
                    <p className={`text-xs mt-1 ${diff > 0 ? 'text-red-600' : diff < 0 ? 'text-green-600' : 'text-slate-400'}`}>
                      {diff > 0 ? '+' : ''}{diff} vs anterior
                    </p>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Top cuentas alertadas + Severidad */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isVisible('top_cuentas') && (
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4">Top Cuentas Alertadas</h3>
            <div className="space-y-2">
              {(data.top_cuentas_alertadas || []).slice(0, 8).map((c) => (
                <div key={c.cuenta_id} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1">
                  <span className="font-mono text-slate-600">{c.cuenta_id}</span>
                  <Badge variant="outline" className="text-xs">{c.alertas} alertas</Badge>
                </div>
              ))}
            </div>
          </Card>
          )}

          {isVisible('severidad') && (
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4">Alertas por Severidad</h3>
            <div className="space-y-3">
              {Object.entries(data.por_severidad || {}).map(([sev, count]) => {
                const maxSev = Math.max(...Object.values(data.por_severidad || {}), 1);
                const colors = {
                  'CRITICA': 'bg-red-500', 'ALTA': 'bg-orange-500',
                  'MEDIA': 'bg-yellow-500', 'BAJA': 'bg-green-500'
                };
                return (
                  <div key={sev} className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 w-16">{sev}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                      <div className={`h-full rounded-full ${colors[sev] || 'bg-slate-400'} flex items-center justify-end pr-2`}
                        style={{ width: `${Math.max((count / maxSev) * 100, 8)}%` }}>
                        <span className="text-[10px] font-bold text-white">{count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
          )}
        </div>

        {/* Activity Heatmap */}
        {isVisible('heatmap') && (
          <ActivityHeatmap />
        )}
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
